import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { MetricCard } from './components/MetricCard';
import { TrendChart } from './components/TrendChart';
import { ExecutiveDataGrid } from './components/ExecutiveDataGrid';
import { useMetricsSummary, useRefreshCache } from '@/hooks/use-executive-query';
import { RefreshCw, BarChart3, TrendingUp, Users, ShoppingCart, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function ExecutiveDashboard() {
  const [timeRange, setTimeRange] = useState<string>('today');
  const [activeMetric, setActiveMetric] = useState<string>('revenue');

  // Fetch summary metrics
  const { data: metrics, isLoading, refetch } = useMetricsSummary(timeRange, [
    'revenue',
    'orders',
    'customers',
    'aov',
  ]);

  const refreshCache = useRefreshCache();

  const handleRefresh = async () => {
    try {
      await refreshCache.mutateAsync({ metrics: ['revenue', 'orders', 'customers', 'aov'], timeRange });
      await refetch();
      toast.success('Dashboard refreshed');
    } catch (error) {
      toast.error('Failed to refresh dashboard');
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-blue-600" />
            Executive Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Real-time insights and analytics</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Time range selector */}
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="mtd">Month to Date</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
            </SelectContent>
          </Select>

          {/* Refresh button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshCache.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshCache.isPending ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Revenue"
          value={metrics?.data?.revenue?.value}
          change={metrics?.data?.revenue?.change}
          changeLabel="vs previous period"
          trend={metrics?.data?.revenue?.trend}
          loading={isLoading}
          format="currency"
        />

        <MetricCard
          title="Total Orders"
          value={metrics?.data?.orders?.value}
          change={metrics?.data?.orders?.change}
          changeLabel="vs previous period"
          trend={metrics?.data?.orders?.trend}
          loading={isLoading}
          format="number"
        />

        <MetricCard
          title="Active Customers"
          value={metrics?.data?.customers?.value}
          change={metrics?.data?.customers?.change}
          changeLabel="vs previous period"
          trend={metrics?.data?.customers?.trend}
          loading={isLoading}
          format="number"
        />

        <MetricCard
          title="Avg Order Value"
          value={metrics?.data?.aov?.value}
          change={metrics?.data?.aov?.change}
          changeLabel="vs previous period"
          trend={metrics?.data?.aov?.trend}
          loading={isLoading}
          format="currency"
        />
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="data">Transaction Data</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Revenue Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  Revenue Trend
                </CardTitle>
                <CardDescription>Last 7 days</CardDescription>
              </CardHeader>
              <CardContent>
                <TrendChart metric="revenue" timeRange="7d" granularity="day" height={250} />
              </CardContent>
            </Card>

            {/* Orders Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                  Orders Trend
                </CardTitle>
                <CardDescription>Last 7 days</CardDescription>
              </CardHeader>
              <CardContent>
                <TrendChart metric="orders" timeRange="7d" granularity="day" height={250} />
              </CardContent>
            </Card>

            {/* Customers Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  Customer Growth
                </CardTitle>
                <CardDescription>Last 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <TrendChart metric="customers" timeRange="30d" granularity="day" height={250} />
              </CardContent>
            </Card>

            {/* Average Order Value Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                  Avg Order Value
                </CardTitle>
                <CardDescription>Last 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <TrendChart metric="aov" timeRange="30d" granularity="day" height={250} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Metric Trends Analysis</CardTitle>
              <CardDescription>Select a metric to view detailed trends</CardDescription>
              <div className="flex items-center gap-2 mt-4">
                <Select value={activeMetric} onValueChange={setActiveMetric}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="orders">Orders</SelectItem>
                    <SelectItem value="customers">Customers</SelectItem>
                    <SelectItem value="aov">Avg Order Value</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <TrendChart
                metric={activeMetric}
                timeRange="30d"
                granularity="day"
                height={400}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transaction Data Tab */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Transaction Data Explorer</CardTitle>
              <CardDescription>
                Ad-hoc query interface with smart routing to HOT/WARM/COLD tiers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ExecutiveDataGrid defaultLimit={50} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Data Freshness Indicator */}
      <div className="flex items-center justify-center">
        <div className="text-xs text-gray-500">
          Last updated: {metrics?.timestamp ? new Date(metrics.timestamp).toLocaleString('id-ID') : '-'}
          {' • '}
          Data refreshes every 60 seconds
        </div>
      </div>
    </div>
  );
}
